import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, TrendingUp, Wallet, Pencil, ArrowRight } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const fmt = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

// ── section 1: Bill Calendar ────────────────────────────────────────────────
const BillCalendar = ({ isEs }) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/planning/bill-calendar?days=60")
            .then((r) => setData(r.data))
            .catch(() => setData({ events: [], total_upcoming: 0, bill_count: 0 }));
    }, []);

    // Build a Month->{day->events} lookup for the next ~2 months.
    const grid = useMemo(() => {
        if (!data?.events) return [];
        const byDay = new Map();
        data.events.forEach((e) => {
            const arr = byDay.get(e.date) || [];
            arr.push(e);
            byDay.set(e.date, arr);
        });
        // Build 2 months from today
        const today = new Date();
        const months = [];
        for (let m = 0; m < 2; m++) {
            const first = new Date(today.getFullYear(), today.getMonth() + m, 1);
            const label = first.toLocaleDateString(isEs ? "es-ES" : "en-US", {
                month: "long", year: "numeric",
            });
            const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
            const startWeekday = (first.getDay() + 6) % 7; // Monday-first
            const cells = [];
            for (let i = 0; i < startWeekday; i++) cells.push(null);
            for (let d = 1; d <= daysInMonth; d++) {
                const iso = `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                cells.push({ day: d, iso, events: byDay.get(iso) || [] });
            }
            months.push({ label, cells });
        }
        return months;
    }, [data, isEs]);

    return (
        <section data-testid="planning-bills" className="rounded-3xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 md:p-8">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-widest font-bold mb-1">
                        <CalendarDays className="w-4 h-4" /> {isEs ? "Calendario de facturas" : "Bill Calendar"}
                    </div>
                    <h2 className="font-display font-extrabold text-2xl md:text-3xl">
                        {data ? fmt(data.total_upcoming) : "…"}{" "}
                        <span className="text-[hsl(var(--muted-foreground))] text-base font-medium">
                            {isEs ? "en los próximos 60 días" : "in the next 60 days"}
                        </span>
                    </h2>
                </div>
                <div className="text-right">
                    <div className="text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-widest font-bold">
                        {isEs ? "Facturas detectadas" : "Bills detected"}
                    </div>
                    <div className="font-display font-extrabold text-2xl">{data?.bill_count ?? 0}</div>
                </div>
            </div>

            {(!data || data.events.length === 0) ? (
                <div className="text-[hsl(var(--muted-foreground))] text-sm py-8 text-center border border-dashed border-[hsl(var(--border))] rounded-xl">
                    {isEs
                        ? "Conecta tu banco vía Plaid para que detectemos tus facturas recurrentes automáticamente."
                        : "Connect your bank via Plaid — we'll detect your recurring bills automatically."}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-6">
                    {grid.map((m) => (
                        <div key={m.label}>
                            <div className="font-display font-bold text-base md:text-lg capitalize mb-2">{m.label}</div>
                            <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] font-bold">
                                {[isEs ? "L" : "M", isEs ? "M" : "T", isEs ? "M" : "W", isEs ? "J" : "T", isEs ? "V" : "F", isEs ? "S" : "S", isEs ? "D" : "S"]
                                    .map((d, i) => <div key={i} className="text-center py-1">{d}</div>)}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                                {m.cells.map((c, i) => c ? (
                                    <div key={i}
                                         data-testid={c.events.length ? `bill-day-${c.iso}` : undefined}
                                         className={`aspect-square rounded-md border text-xs flex flex-col items-center justify-start p-1 ${
                                            c.events.length
                                                ? "border-emerald-400/40 bg-emerald-500/10"
                                                : "border-[hsl(var(--border))] bg-transparent"
                                         }`}
                                         title={c.events.map(e => `${e.merchant} · ${fmt(e.amount)}`).join("\n")}
                                    >
                                        <div className="font-bold">{c.day}</div>
                                        {c.events.slice(0, 2).map((e, j) => (
                                            <div key={j} className="text-[9px] leading-tight text-emerald-700 dark:text-emerald-300 truncate w-full text-center">
                                                {fmt(e.amount)}
                                            </div>
                                        ))}
                                    </div>
                                ) : <div key={i} />)}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

// ── section 2: Budget Rollover ──────────────────────────────────────────────
const catLabel = (cat, isEs) => {
    const map = {
        housing:        [isEs ? "Vivienda" : "Housing"],
        food_dining:    [isEs ? "Comida y restaurantes" : "Food & dining"],
        transportation: [isEs ? "Transporte" : "Transportation"],
        bills_utilities:[isEs ? "Servicios" : "Utilities"],
        entertainment:  [isEs ? "Entretenimiento" : "Entertainment"],
        subscriptions:  [isEs ? "Suscripciones" : "Subscriptions"],
        shopping:       [isEs ? "Compras" : "Shopping"],
        savings_target: [isEs ? "Meta de ahorro" : "Savings target"],
    };
    return map[cat]?.[0] || cat;
};

const BudgetRow = ({ b, isEs, onSave }) => {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(String(b.monthly_limit));
    const pctSpent = b.monthly_limit > 0 ? Math.min(100, (b.spent_this_month / b.monthly_limit) * 100) : 0;
    const overspent = b.spent_this_month > b.monthly_limit;
    const availableColor = b.available < 0 ? "text-red-500" : b.available < b.monthly_limit * 0.25 ? "text-amber-500" : "text-emerald-500";

    return (
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={`budget-row-${b.category}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="font-display font-bold text-base md:text-lg capitalize">{catLabel(b.category, isEs)}</div>
                {editing ? (
                    <div className="flex items-center gap-1">
                        <Input
                            data-testid={`budget-input-${b.category}`}
                            value={val}
                            onChange={(e) => setVal(e.target.value)}
                            className="w-24 h-8 text-sm"
                        />
                        <Button
                            data-testid={`budget-save-${b.category}`}
                            size="sm"
                            className="h-8"
                            onClick={() => { onSave(b.category, parseFloat(val) || 0); setEditing(false); }}
                        >
                            {isEs ? "Guardar" : "Save"}
                        </Button>
                    </div>
                ) : (
                    <button
                        data-testid={`budget-edit-${b.category}`}
                        onClick={() => setEditing(true)}
                        className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] p-1"
                    >
                        <Pencil className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))] mb-1">
                <span>{fmt(b.spent_this_month)} / {fmt(b.monthly_limit)}</span>
                {b.rollover_in > 0 && (
                    <span className="text-emerald-500 font-bold" data-testid={`budget-rollover-${b.category}`}>
                        +{fmt(b.rollover_in)} {isEs ? "acumulado" : "rolled over"}
                    </span>
                )}
            </div>
            <div className="h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
                <div className={`h-full transition-all ${overspent ? "bg-red-500" : "bg-emerald-500"}`}
                     style={{ width: `${pctSpent}%` }} />
            </div>
            <div className={`mt-2 text-sm font-bold ${availableColor}`}>
                {b.available >= 0 ? (isEs ? "Disponible" : "Available") : (isEs ? "Sobrepasado por" : "Over by")}: {fmt(Math.abs(b.available))}
            </div>
        </div>
    );
};

const BudgetRollover = ({ isEs }) => {
    const [data, setData] = useState(null);
    const load = () => api.get("/planning/budgets").then((r) => setData(r.data)).catch(() => setData({ budgets: [] }));
    useEffect(() => { load(); }, []);

    const save = async (category, monthly_limit) => {
        await api.post("/planning/budgets", { category, monthly_limit });
        load();
    };

    return (
        <section data-testid="planning-budgets" className="rounded-3xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 md:p-8">
            <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-widest font-bold mb-1">
                <Wallet className="w-4 h-4" /> {isEs ? "Presupuesto con arrastre" : "Budget Rollover"}
            </div>
            <h2 className="font-display font-extrabold text-2xl md:text-3xl mb-1">
                {isEs ? "Lo no gastado suma al siguiente mes" : "Unspent budget rolls to next month"}
            </h2>
            <p className="text-[hsl(var(--muted-foreground))] text-sm mb-5">
                {isEs
                    ? "Semilla inicial basada en la regla 50/30/20 sobre tu ingreso promedio. Ajusta cualquier categoría."
                    : "Seeded from a 50/30/20 split of your average income. Edit any category to your taste."}
            </p>
            {(!data || data.budgets.length === 0) ? (
                <div className="text-[hsl(var(--muted-foreground))] text-sm py-8 text-center border border-dashed border-[hsl(var(--border))] rounded-xl">
                    {isEs
                        ? "Necesitamos 1-2 meses de transacciones para calibrar tus presupuestos. Conecta tu banco primero."
                        : "We need 1-2 months of transactions to calibrate your budgets. Connect your bank first."}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-3">
                    {data.budgets.map((b) => (
                        <BudgetRow key={b.category} b={b} isEs={isEs} onSave={save} />
                    ))}
                </div>
            )}
        </section>
    );
};

// ── section 3: Cash-Flow Forecast ───────────────────────────────────────────
const ForecastChart = ({ projections }) => {
    if (!projections?.length) return null;
    const bals = projections.map((p) => p.cumulative_balance);
    const min = Math.min(0, ...bals);
    const max = Math.max(...bals, 1);
    const range = max - min || 1;
    const width = 600, height = 160, pad = 20;
    const step = (width - pad * 2) / Math.max(1, projections.length - 1);
    const y = (v) => height - pad - ((v - min) / range) * (height - pad * 2);
    const points = projections.map((p, i) => `${pad + i * step},${y(p.cumulative_balance)}`).join(" ");
    const zeroY = y(0);

    return (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-40" preserveAspectRatio="none">
            <defs>
                <linearGradient id="forecastFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(16,185,129,0.35)" />
                    <stop offset="100%" stopColor="rgba(16,185,129,0)" />
                </linearGradient>
            </defs>
            <line x1={pad} y1={zeroY} x2={width - pad} y2={zeroY} stroke="currentColor" strokeOpacity="0.15" strokeDasharray="3 3" />
            <polygon points={`${pad},${height - pad} ${points} ${width - pad},${height - pad}`} fill="url(#forecastFill)" />
            <polyline points={points} fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinejoin="round" />
            {projections.map((p, i) => (
                <circle key={i} cx={pad + i * step} cy={y(p.cumulative_balance)} r="3.5" fill="#10B981" />
            ))}
        </svg>
    );
};

const CashFlowForecast = ({ isEs }) => {
    const [months, setMonths] = useState(6);
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/planning/forecast?months=${months}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null));
    }, [months]);

    const endBal = data?.projections?.length ? data.projections[data.projections.length - 1].cumulative_balance : 0;
    const trending = data?.projections?.length && data.projections[0].net > 0;

    return (
        <section data-testid="planning-forecast" className="rounded-3xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-6 md:p-8">
            <div className="flex items-start justify-between gap-3 mb-1">
                <div>
                    <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] text-xs uppercase tracking-widest font-bold mb-1">
                        <TrendingUp className="w-4 h-4" /> {isEs ? "Pronóstico de flujo" : "Cash-Flow Forecast"}
                    </div>
                    <h2 className="font-display font-extrabold text-2xl md:text-3xl">
                        {isEs ? "Balance proyectado en" : "Projected balance in"} {months} {isEs ? "meses" : "months"}
                    </h2>
                    <div className={`text-xl md:text-2xl font-black mt-1 ${endBal >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                        {fmt(endBal)}
                    </div>
                </div>
                <div className="flex gap-1">
                    {[3, 6, 12].map((m) => (
                        <button key={m}
                            data-testid={`forecast-months-${m}`}
                            onClick={() => setMonths(m)}
                            className={`text-xs font-bold rounded-full px-3 py-1 border ${
                                months === m
                                    ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] border-transparent"
                                    : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"
                            }`}
                        >
                            {m}M
                        </button>
                    ))}
                </div>
            </div>

            {!data || data.projections.length === 0 || data.starting_balance === 0 && data.projections[0].projected_income === 0 ? (
                <div className="text-[hsl(var(--muted-foreground))] text-sm py-8 text-center border border-dashed border-[hsl(var(--border))] rounded-xl">
                    {isEs
                        ? "Necesitamos historial de transacciones para proyectar tu flujo. Conecta tu banco."
                        : "We need transaction history to forecast your cash flow. Connect your bank first."}
                </div>
            ) : (
                <>
                    <div className="text-[hsl(var(--muted-foreground))] mt-3">
                        <ForecastChart projections={data.projections} />
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                        <div className="rounded-xl border border-[hsl(var(--border))] p-3">
                            <div className="text-[hsl(var(--muted-foreground))] uppercase tracking-widest font-bold text-[10px]">
                                {isEs ? "Ingreso mensual" : "Monthly income"}
                            </div>
                            <div className="font-black text-base mt-0.5">{fmt(data.projections[0].projected_income)}</div>
                        </div>
                        <div className="rounded-xl border border-[hsl(var(--border))] p-3">
                            <div className="text-[hsl(var(--muted-foreground))] uppercase tracking-widest font-bold text-[10px]">
                                {isEs ? "Gastos mensual" : "Monthly expenses"}
                            </div>
                            <div className="font-black text-base mt-0.5">{fmt(data.projections[0].projected_expenses)}</div>
                        </div>
                        <div className="rounded-xl border border-[hsl(var(--border))] p-3">
                            <div className="text-[hsl(var(--muted-foreground))] uppercase tracking-widest font-bold text-[10px]">
                                {isEs ? "Neto por mes" : "Net per month"}
                            </div>
                            <div className={`font-black text-base mt-0.5 ${trending ? "text-emerald-500" : "text-red-500"}`}>
                                {fmt(data.projections[0].net)}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

// ── page shell ──────────────────────────────────────────────────────────────
export default function Planning() {
    const { language } = useApp();
    const isEs = language === "es";
    return (
        <AppLayout title={t(language, "nav_planning")}>
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="space-y-6 md:space-y-8"
                data-testid="planning-page"
            >
                <BillCalendar isEs={isEs} />
                <BudgetRollover isEs={isEs} />
                <CashFlowForecast isEs={isEs} />
            </motion.div>
        </AppLayout>
    );
}
